import {
  Box,
  BoxProps,
  ButtonBase,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
  PopoverProps,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useAppId } from "../../hook";
import { useSelectedNotebookPageIdMap } from "../../hook/page";
import {
  useFetchPagePropertyValuesListener,
  useFolderPropertyTypes,
  useGetProperty,
  useGetPropertyValue,
  usePropertyValue,
  useSetPropertyValue,
} from "../../hook/page/property";
import { PropertyTypeKey, propValueToText } from "../../types/page/property";
import { DynamicTextField } from "../dynamicTextField";
import { PropertyTypeSelect } from "../../types/page/propertyType/select";
import { useMenu } from "../../hook/menu";

export const NotePropertyValueText: React.FC<{
  propName: string;
  pageId: string;
}> = ({ propName, pageId }) => {
  const getPropVal = useGetPropertyValue();
  // One shared rule for every renderer — see propValueToText (types/page/property.ts).
  const propVal = propValueToText(getPropVal(pageId, propName));
  const setPropVal = useSetPropertyValue();
  const [localVal, setLocalVal] = useState<string | undefined>(undefined);
  const localDefined = localVal !== undefined;
  const localText = localVal ?? "";

  useEffect(() => {
    if (!localDefined && propVal !== "") {
      setLocalVal(propVal);
    }
  }, [propVal, localDefined]);

  const dispatchCallBack = useMemo(
    () => debounce((propVal) => setPropVal(pageId, propName, propVal), 250),
    [pageId, propName],
  );

  const onChange = (value: string) => {
    setLocalVal(value);
    dispatchCallBack(value);
  };

  return <DynamicTextField text={localText} setText={onChange} />;
};

export const NotePropertyValueSelectMenu: React.FC<
  MenuProps & {
    propName: string;
    pageId: string;
    folderId: string;
  }
> = ({ propName, folderId, pageId, ...menuProps }) => {
  const { onClose, anchorEl } = menuProps;
  const getProp = useGetProperty();
  const prop = getProp(folderId, propName);
  const selectProp = prop as PropertyTypeSelect;
  const [propVal, setPropVal] = usePropertyValue(pageId, propName);
  // One shared rule — see propValueToText (types/page/property.ts). The old
  // `propValSelect?.["prop-value"]` indexed a plain STRING, so this was always ""
  // and the menu never showed the row's current option (A.20a / U.6).
  const selectVal = propValueToText(propVal);
  const options = selectProp.options;
  const [appId] = useAppId();

  const onChange = (value: string) => {
    if (!appId) {
      throw new Error("No App Id!");
    }

    setPropVal(value);
    onClose?.({}, "backdropClick");
  };

  return (
    <Menu {...menuProps}>
      <MenuItem disabled sx={{ width: "15rem" }}>
        <ListItemText>Select Value</ListItemText>
      </MenuItem>
      <Box sx={{ padding: "1rem", width: "15rem" }}>
        <Select
          open={anchorEl != null}
          onClose={() => {
            onClose?.({}, "backdropClick");
          }}
          onChange={(e) => onChange(e.target.value)}
          value={selectVal}
          sx={{ width: "100%" }}
        >
          {options.map((option) => {
            return (
              <MenuItem key={option.label} value={option.label}>
                {option.label}
              </MenuItem>
            );
          })}
        </Select>
      </Box>
    </Menu>
  );
};

export const NotePropertyValueSelect: React.FC<
  {
    pageId: string;
    propName: string;
    folderId: string;
  } & BoxProps
> = ({ folderId, pageId, propName, ...props }) => {
  const getProp = useGetProperty();
  const prop = getProp(folderId, propName);
  const [propVal, setPropVal] = usePropertyValue(pageId, propName);
  // One shared rule for every renderer — see propValueToText (types/page/property.ts).
  // It returns "" for undefined/null, so "has a value" is a single comparison: a select
  // whose option label is "0" or "false" is a real value and must not be styled as empty.
  const selectVal = propValueToText(propVal);
  const selectHasValue = selectVal !== "";
  const theme = useTheme();
  const {
    anchorEl: anchorElSelectValue,
    handleOpen: handleOpenSelectValue,
    onClose: onCloseSelectValue,
    open: openSelectValue,
    setAnchorEl: setAnchorElSelectValue,
    anchorToClick: anchorToClickSelectValue,
  } = useMenu();

  // useEffect(() => {
  //   if (!anchorEl) return;
  //   // setOpen(true);
  // }, [anchorEl]);

  if (!prop) return <></>;

  return (
    <Box {...props}>
      <NotePropertyValueSelectMenu
        open={openSelectValue}
        anchorEl={anchorElSelectValue}
        onClose={onCloseSelectValue}
        propName={propName}
        pageId={pageId}
        folderId={folderId}
      />
      <ButtonBase
        onClick={handleOpenSelectValue}
        // ref={(ref) => setAnchorEl(ref)}
        sx={{
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          padding: "1rem",
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "left",
        }}
      >
        <Box>
          <Typography
            sx={{
              color: selectHasValue ? "inherit" : theme.palette.text.disabled,
            }}
          >
            {selectHasValue ? selectVal : "No Value"}
          </Typography>
        </Box>
      </ButtonBase>
    </Box>
  );
};

export const NotePropertyValue: React.FC<{
  folderId: string;
  pageId: string;
  propName: string;
}> = ({ pageId, folderId, propName }) => {
  const getProp = useGetProperty();
  const prop = getProp(folderId, propName);
  const propType = prop?.["prop-type"];

  if (!propType) {
    return <></>;
  }

  switch (propType) {
    case PropertyTypeKey.TEXT:
      return <NotePropertyValueText propName={propName} pageId={pageId} />;
    case PropertyTypeKey.SELECT:
      return (
        <NotePropertyValueSelect
          folderId={folderId}
          propName={propName}
          pageId={pageId}
        />
      );
  }
};

export const NoteProperties: React.FC = () => {
  const { selectedNotebookPageId: pageId, selectedNotebookPage } =
    useSelectedNotebookPageIdMap();
  const folderId = selectedNotebookPage?.["folder-id"];
  const { folderProps } = useFolderPropertyTypes(folderId);
  useFetchPagePropertyValuesListener();

  if (!pageId) return <></>;

  return (
    <>
      {Object.values(folderProps ?? {}).map((prop) => {
        if (!folderId) return <></>;

        return (
          <Box
            key={prop.name}
            sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <Box sx={{ width: "15rem" }}>{prop.name}</Box>
            <Box sx={{ display: "flex", flexGrow: 1 }}>
              <NotePropertyValue
                folderId={folderId}
                propName={prop.name}
                pageId={pageId}
              />
            </Box>
          </Box>
        );
      })}
    </>
  );
};
